
import React, { useState } from "react";
import { Helmet } from "react-helmet";
import Layout from "@/components/Layout";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { 
  AlertTriangle, 
  Bell, 
  CheckCircle, 
  Clock, 
  Eye, 
  MapPin, 
  MessageSquare, 
  Shield, 
  User, 
  Users
} from "lucide-react";
import { toast } from "@/hooks/use-toast";

const EmergencyPage: React.FC = () => {
  const [isRequestSubmitted, setIsRequestSubmitted] = useState(false);
  const [seniorName, setSeniorName] = useState("");
  const [address, setAddress] = useState("");
  const [urgency, setUrgency] = useState("medium");
  const [notes, setNotes] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Simulate request submission
    setTimeout(() => {
      setIsRequestSubmitted(true);
      toast({
        title: "Emergency check-in requested",
        description: "Local volunteers have been notified and will respond shortly.",
        variant: "default",
      });
    }, 1500);
  };

  const urgencyLevels = [
    { value: "low", label: "Low", description: "Routine check-in" },
    { value: "medium", label: "Medium", description: "No response to calls" },
    { value: "high", label: "High", description: "Potential emergency" },
  ];

  const volunteers = [
    { name: "Michael Johnson", distance: "0.7 miles", status: "Available", avatar: "M" },
    { name: "Emma Wilson", distance: "1.2 miles", status: "Available", avatar: "E" },
    { name: "David Lee", distance: "1.5 miles", status: "Available", avatar: "D" },
    { name: "Sarah Miller", distance: "2.1 miles", status: "Away", avatar: "S" },
  ];

  return (
    <Layout>
      <Helmet>
        <title>Emergency Check-In | CareConnect</title>
        <meta name="description" content="Request emergency check-ins from local volunteers to ensure the safety and well-being of your elderly loved ones." />
      </Helmet>

      {/* Emergency Hero */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-care-50 to-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-block py-1 px-3 rounded-full text-sm font-medium bg-amber-100 text-amber-800 mb-4">
                Community Support
              </span>
              
              <h1 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight text-balance">
                Emergency check-in <span className="title-gradient">volunteer network</span>
              </h1>
              
              <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto text-balance">
                Request a wellness check from nearby community volunteers when you're unable to reach your elderly loved one or suspect they might need assistance.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Request Form */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="glass-morphism rounded-xl p-8 h-full">
                {!isRequestSubmitted ? (
                  <>
                    <h2 className="text-2xl font-bold mb-6 text-care-800">Request an Emergency Check-In</h2>
                    
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div>
                        <label htmlFor="seniorName" className="block text-sm font-medium text-gray-700 mb-1">
                          Senior's Name
                        </label>
                        <input
                          type="text"
                          id="seniorName"
                          className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-care-500"
                          placeholder="Enter full name"
                          value={seniorName}
                          onChange={(e) => setSeniorName(e.target.value)}
                          required
                        />
                      </div>
                      
                      <div>
                        <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
                          Address
                        </label>
                        <input
                          type="text"
                          id="address"
                          className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-care-500"
                          placeholder="Enter full address"
                          value={address}
                          onChange={(e) => setAddress(e.target.value)}
                          required
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                          Urgency Level
                        </label>
                        <div className="grid grid-cols-3 gap-4">
                          {urgencyLevels.map((level) => (
                            <div
                              key={level.value}
                              className={`border rounded-lg p-3 cursor-pointer transition-colors text-center ${
                                urgency === level.value
                                  ? "border-care-500 bg-care-50"
                                  : "border-gray-200 hover:border-gray-300"
                              }`}
                              onClick={() => setUrgency(level.value)}
                            >
                              <div className="font-medium mb-1">{level.label}</div>
                              <div className="text-xs text-muted-foreground">{level.description}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      <div>
                        <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
                          Additional Notes
                        </label>
                        <textarea
                          id="notes"
                          rows={4}
                          className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-care-500"
                          placeholder="Provide any relevant information like access instructions, concerns, etc."
                          value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                        ></textarea>
                      </div>
                      
                      <Button 
                        type="submit" 
                        className="w-full py-3 bg-care-600 hover:bg-care-700 text-white font-medium rounded-lg transition-colors"
                      >
                        Submit Emergency Request
                      </Button>
                      
                      <p className="text-sm text-muted-foreground text-center mt-4">
                        By submitting this request, nearby verified volunteers will be notified to check on your loved one as soon as possible.
                      </p>
                    </form>
                  </>
                ) : (
                  <div className="text-center py-8">
                    <div className="bg-green-50 text-green-800 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6">
                      <CheckCircle className="h-8 w-8" />
                    </div>
                    <h2 className="text-2xl font-bold mb-4 text-care-800">Request Submitted Successfully</h2>
                    <p className="text-muted-foreground mb-8">
                      Your emergency check-in request has been sent to nearby volunteers. You will receive updates as volunteers respond.
                    </p>
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-8 text-left">
                      <div className="flex">
                        <AlertTriangle className="h-5 w-5 text-amber-600 mr-2 flex-shrink-0 mt-0.5" />
                        <div>
                          <h3 className="font-medium text-amber-800 mb-1">Important Information</h3>
                          <p className="text-sm text-amber-700">
                            For medical emergencies, please call 911 immediately. Volunteer check-ins are not a substitute for emergency medical services.
                          </p>
                        </div>
                      </div>
                    </div>
                    <Button 
                      className="bg-care-600 hover:bg-care-700 text-white font-medium"
                      onClick={() => setIsRequestSubmitted(false)}
                    >
                      Submit Another Request
                    </Button>
                  </div>
                )}
              </div>
            </motion.div>
            
            {/* Nearby Volunteers */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="space-y-10">
                <div className="bg-white rounded-xl border border-gray-200 p-8 shadow-sm">
                  <h2 className="text-xl font-bold mb-6 flex items-center text-care-800">
                    <Users className="h-5 w-5 mr-2 text-care-600" />
                    Nearby Volunteer Network
                  </h2>
                  
                  <div className="space-y-4 mb-6">
                    {volunteers.map((volunteer, index) => (
                      <motion.div
                        key={index}
                        className="flex items-center justify-between p-3 rounded-lg border border-gray-100 hover:border-care-200 transition-colors"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 + (index * 0.1) }}
                      >
                        <div className="flex items-center">
                          <div className="h-10 w-10 rounded-full bg-care-100 flex items-center justify-center text-care-800 font-medium mr-3">
                            {volunteer.avatar}
                          </div>
                          <div>
                            <h3 className="font-medium text-gray-900">{volunteer.name}</h3>
                            <div className="flex items-center text-sm text-muted-foreground">
                              <MapPin className="h-3 w-3 mr-1" />
                              <span>{volunteer.distance}</span>
                            </div>
                          </div>
                        </div>
                        <span className={`text-sm ${
                          volunteer.status === "Available" 
                            ? "text-green-600" 
                            : "text-amber-600"
                        }`}>
                          {volunteer.status}
                        </span>
                      </motion.div>
                    ))}
                  </div>
                  
                  <div className="flex items-center justify-between p-3 rounded-lg bg-care-50 text-sm">
                    <span>Currently active volunteers</span>
                    <span className="font-medium">3 of 4</span>
                  </div>
                </div>
                
                <div className="bg-white rounded-xl border border-gray-200 p-8 shadow-sm">
                  <h2 className="text-xl font-bold mb-6 flex items-center text-care-800">
                    <Shield className="h-5 w-5 mr-2 text-care-600" />
                    How It Works
                  </h2>
                  
                  <div className="space-y-6">
                    <div className="flex">
                      <div className="bg-care-100 h-8 w-8 rounded-full flex items-center justify-center text-care-800 font-medium mr-4 flex-shrink-0">
                        1
                      </div>
                      <div>
                        <h3 className="font-medium mb-1 text-care-800">Submit a Request</h3>
                        <p className="text-sm text-muted-foreground">
                          Provide details about the senior you're concerned about and the urgency level.
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex">
                      <div className="bg-care-100 h-8 w-8 rounded-full flex items-center justify-center text-care-800 font-medium mr-4 flex-shrink-0">
                        2
                      </div>
                      <div>
                        <h3 className="font-medium mb-1 text-care-800">Volunteers Notified</h3>
                        <p className="text-sm text-muted-foreground">
                          Available volunteers in the area receive an alert on their devices.
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex">
                      <div className="bg-care-100 h-8 w-8 rounded-full flex items-center justify-center text-care-800 font-medium mr-4 flex-shrink-0">
                        3
                      </div>
                      <div>
                        <h3 className="font-medium mb-1 text-care-800">Quick Response</h3>
                        <p className="text-sm text-muted-foreground">
                          A volunteer accepts the request and proceeds to check on the senior.
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex">
                      <div className="bg-care-100 h-8 w-8 rounded-full flex items-center justify-center text-care-800 font-medium mr-4 flex-shrink-0">
                        4
                      </div>
                      <div>
                        <h3 className="font-medium mb-1 text-care-800">Status Updates</h3>
                        <p className="text-sm text-muted-foreground">
                          You receive updates about the check-in and the senior's status.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Benefits Section */}
      <section className="py-20 bg-care-50">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block py-1 px-3 rounded-full text-sm font-medium bg-care-100 text-care-800 mb-4">
              Peace of Mind
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 tracking-tight text-balance">
              Benefits of our volunteer network
            </h2>
            <p className="text-muted-foreground text-lg mx-auto text-balance">
              Our community-based approach ensures quick responses and reliable support for elderly individuals during times of concern.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <Clock className="h-10 w-10" />,
                title: "Quick Response Time",
                description: "Volunteers are typically able to respond within minutes, providing rapid wellness checks when needed."
              },
              {
                icon: <MapPin className="h-10 w-10" />,
                title: "Local Community Network",
                description: "Nearby volunteers who know the neighborhood can provide contextual support and familiarity."
              },
              {
                icon: <Bell className="h-10 w-10" />,
                title: "Real-time Notifications",
                description: "Receive immediate updates when a volunteer accepts and completes the check-in request."
              },
              {
                icon: <Eye className="h-10 w-10" />,
                title: "Visual Verification",
                description: "Get confirmation that your loved one is safe through verified check-ins and status reports."
              },
              {
                icon: <User className="h-10 w-10" />,
                title: "Vetted Volunteers",
                description: "All community volunteers undergo background checks and training for safety and reliability."
              },
              {
                icon: <MessageSquare className="h-10 w-10" />,
                title: "Direct Communication",
                description: "Easy communication with volunteers through our secure in-app messaging system."
              }
            ].map((benefit, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-xl p-6 shadow-sm"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ 
                  duration: 0.5,
                  delay: index * 0.1,
                  ease: [0.45, 0, 0.2, 1]
                }}
              >
                <div className="p-4 bg-care-50 rounded-xl w-16 h-16 flex items-center justify-center mb-4 text-care-600">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-semibold mb-2 text-care-800">{benefit.title}</h3>
                <p className="text-muted-foreground text-balance">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <div className="bg-care-600 text-white rounded-2xl p-8 md:p-12 overflow-hidden relative">
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-care-500 rounded-full opacity-30" />
            <div className="absolute -bottom-20 -left-20 w-60 h-60 bg-care-500 rounded-full opacity-20" />
            
            <div className="relative z-10 max-w-3xl mx-auto text-center">
              <h2 className="text-3xl font-bold mb-4 text-white text-balance">
                Ready to join our volunteer network?
              </h2>
              <p className="text-white/90 mb-8 text-balance">
                Help create a supportive community for elderly individuals in your neighborhood. Become a volunteer and make a difference.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Button className="bg-white text-care-700 hover:bg-gray-100 font-medium py-3 px-6 rounded-full">
                  Apply as Volunteer
                </Button>
                <Button variant="outline" className="text-white border-white hover:bg-care-500 font-medium py-3 px-6 rounded-full">
                  Learn More
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default EmergencyPage;
